import { takeLatest, put, call, cancelled } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import * as R from 'ramda';
import moment from 'moment';

import {
  questionnaire as questionnaireSchema,
  questionnaires as questionnairesSchema,
} from '../schema/questionnaire';

import {
  REQUEST_QUESTIONNAIRELIST_START,
  REQUEST_QUESTIONNAIRE_START,
  UPDATE_QUESTIONNAIRE_START,
  UPLOAD_FILE_QUESTIONNAIRE_START,
  DELETE_FILE_QUESTIONNAIRE_START,
  SEND_QUESTIONNAIRE_START,
  QUESTIONNAIRE_ASSIGNUSER_START,
  REJECT_QUESTIONNAIRE_START,
  APPROVE_QUESTIONNAIRE_START,
  REQUEST_REVIEWING_QUESTIONNAIRES_START,
  SET_QUESTIONNAIRE_TO_DRAFT_START,
} from '../actions';

angular.module('tct.sagas.questionnaire', [])
  .factory('questionnaireSagaFactory', questionnaireSagaFactory);

questionnaireSagaFactory.$inject = [
  'actionsFactory',
  'companiesService',
  'questionnaireService',
  'uploadService',
  'mappingService',
  'questionnairesService',
  '$state',
];

function questionnaireSagaFactory(
  actionsFactory,
  companiesService,
  questionnaireService,
  uploadService,
  mappingService,
  questionnairesService,
  $state) {
  const factory = {
    questionnaireFlow,
    assignContactFlow,
  };

  return factory;

  function* getQuestionnaireList(action) {
    try {
      const response =
        yield call(companiesService.getCompanyQuestionnaireList, action.companyId);
      const mappedQuestionnaires = mappingService.objectKeysToCamel(
        (response.data && response.data.data) || []);

      yield put(actionsFactory.reqQuestionnaireListSuccess({
        items: normalize(mappedQuestionnaires, questionnairesSchema),
        companyId: action.companyId,
      }));
      return response;
    } catch (err) {
      yield;
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return action;
  }

  function initContentWhenNeeded(content, template) {
    const fields = R.indexBy(R.prop('id'), R.filter(R.propEq('type', 'field'), content));
    const groups = R.indexBy(R.prop('id'), R.filter(R.propEq('type', 'group'), content));

    const items = R.chain(R.prop('items'), template.sections);
    items.forEach((item) => {
      if (item.type === 'field') {
        if (!fields[item.id]) {
          fields[item.id] = { id: item.id, type: item.type, name: item.name, value: null };
        }
      } else if (item.type === 'group') {
        if (!groups[item.id]) {
          groups[item.id] = {
            id: item.id,
            type: item.type,
            fields: [getInitialGroupItemFromTemplate(item)],
          };
        }
      }
    });

    return R.concat(R.values(fields), R.values(groups));
  }

  function getInitialGroupItemFromTemplate(groupTemplate) {
    return R.map(item => ({
      id: item.id,
      type: item.type,
      name: item.name,
      value: null,
    }), groupTemplate.fields || []);
  }

  function getNormalizedQuestionnaire(response) {
    const normalisedQuestionnaire = normalize(
      mappingService.objectKeysToCamel(response.data && response.data.data),
      questionnaireSchema);

    const questionnaire = normalisedQuestionnaire.entities.questionnaires[
      normalisedQuestionnaire.result
    ];

    const mappedContent = initContentWhenNeeded(questionnaire.content,
      normalisedQuestionnaire.entities.questionnaireTemplates[questionnaire.template]);

    mappedContent.forEach((item) => {
      if (item.type !== 'group' && typeof item.date !== 'undefined') {
        item.date = moment(item.date, 'YYYY-MM-DD HH:mm:ssZZ').toDate();
      }
      if (item.type === 'group') {
        item.fields.forEach((row) => {
          row.forEach((field) => {
            if (typeof field.date !== 'undefined') {
              field.date = moment(field.date, 'YYYY-MM-DD HH:mm:ssZZ').toDate();
            }
          });
        });
      }
    });

    mappedContent.forEach((item) => {
      if (item.type === 'group') {
        item.fields = R.map(row => R.indexBy(R.prop('id'), row), item.fields);
      }
    });

    const groups = R.indexBy(R.prop('id'), R.filter(R.propEq('type', 'group'), mappedContent));
    const fields = R.indexBy(R.prop('id'), R.filter(R.propEq('type', 'field'), mappedContent));

    questionnaire.fields = fields;
    questionnaire.groups = groups;

    if (questionnaire.manualScores) {
      questionnaire.manualScores = R.indexBy(R.prop('id'), questionnaire.manualScores);
    }

    normalisedQuestionnaire.entities.questionnaires[
      normalisedQuestionnaire.result
    ] = questionnaire;

    return normalisedQuestionnaire;
  }

  function* getQuestionnaire(action) {
    try {
      const response =
        yield call(questionnaireService.getQuestionnaire, action.questionnaireId);

      const normalisedQuestionnaire = getNormalizedQuestionnaire(response);

      yield put(actionsFactory.reqQuestionnaireSuccess({
        response: normalisedQuestionnaire,
        companyId: action.companyId,
      }));
      return response;
    } catch (err) {
      yield;
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return action;
  }

  function* updateQuestionnaire(action) {
    try {
      const response = yield call(
        questionnaireService.updateQuestionnaire,
        action.questionnaireId, action.content,
        action.score,
        action.notes);

      const normalisedQuestionnaire = getNormalizedQuestionnaire(response);

      yield put(actionsFactory.updateQuestionnaireSuccess({
        response: normalisedQuestionnaire,
        companyId: action.companyId,
      }));

      return response;
    } catch (err) {
      yield put(actionsFactory.updateQuestionnaireError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return action;
  }

  function* uploadFileQuestionnaire(action) {
    try {
      const uploadFileRes = yield call(uploadService.uploadFile, action.file);
      const uploadedFile = uploadFileRes.data && uploadFileRes.data.data;
      yield put(actionsFactory.uploadFileQuestionnaireSuccess(
        uploadedFile, action.dataset, action.id));
      return uploadFileRes;
    } catch (err) {
      yield put(actionsFactory.uploadFileQuestionnaireError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return action;
  }

  function* deleteFileQuestionnaire(action) {
    try {
      yield put(actionsFactory.deleteFileQuestionnaireSuccess(action.data, action.id));
      return action;
    } catch (err) {
      yield put(actionsFactory.deleteFileQuestionnaireError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* sendQuestionnaire(action) {
    try {
      const response = yield call(
        questionnaireService.sendQuestionnaire, action.questionnaireId, action.content);

      const normalisedQuestionnaire = getNormalizedQuestionnaire(response);

      yield put(actionsFactory.sendQuestionnaireSuccess({
        response: normalisedQuestionnaire,
      }));
      return response;
    } catch (err) {
      yield put(actionsFactory.sendQuestionnaireError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* assignContact(action) {
    try {
      const response =
        yield call(questionnairesService.assignContactUser, action.questionnaireId, action.userId);

      yield put(actionsFactory.assignContactUserSuccess(
        normalize(
          mappingService.objectKeysToCamel(response.data && response.data.data),
          questionnaireSchema)));

      return response;
    } catch (err) {
      yield put(actionsFactory.reqOrdersError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* rejectQuestionnaire(action) {
    try {
      const response =
        yield call(questionnaireService.rejectQuestionnaire, action.questionnaireId);

      const normalisedQuestionnaire = getNormalizedQuestionnaire(response);

      yield put(actionsFactory.rejectQuestionnaireSuccess({ response: normalisedQuestionnaire }));
      return response;
    } catch (err) {
      yield put(actionsFactory.rejectQuestionnaireError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* approveQuestionnaire(action) {
    try {
      const response = yield call(
        questionnaireService.approveQuestionnaire,
        action.questionnaireId, action.content,
        mappingService.objectKeysToSnake(R.values(action.manualScores || [])));

      const normalisedQuestionnaire = getNormalizedQuestionnaire(response);

      yield put(actionsFactory.approveQuestionnaireSuccess({
        response: normalisedQuestionnaire,
        companyId: action.companyId,
      }));

      $state.go('authenticated.home');

      return response;
    } catch (err) {
      yield put(actionsFactory.approveQuestionnaireError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* getReviewingQuestionnaires(action) {
    try {
      const response = yield call(questionnaireService.getReviewingQuestionnaires, action.params);
      const pagination = mappingService.objectKeysToCamel(response.data && response.data.data);

      yield put(actionsFactory.reqReviewingQuestionnairesSuccess({
        items: normalize(pagination.items, questionnairesSchema),
        total: pagination.totalItems,
        page: pagination.page,
      }));

      return response;
    } catch (err) {
      yield put(
        actionsFactory.reqReviewingQuestionnairesError((err.data && err.data.error) || err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* setQuestionnaireToDraft(action) {
    try {
      const response = yield call(
        questionnaireService.setQuestionnaireToDraft,
        action.questionnaireId, action.message);

      const normalisedQuestionnaire = getNormalizedQuestionnaire(response);

      yield put(
        actionsFactory.setQuestionnaireToDraftSuccess({ response: normalisedQuestionnaire }));
      return response;
    } catch (err) {
      yield put(actionsFactory.setQuestionnaireToDraftError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* assignContactFlow() {
    yield takeLatest(QUESTIONNAIRE_ASSIGNUSER_START, assignContact);
  }

  function* questionnaireFlow() {
    yield takeLatest(REQUEST_QUESTIONNAIRELIST_START, getQuestionnaireList);
    yield takeLatest(REQUEST_QUESTIONNAIRE_START, getQuestionnaire);
    yield takeLatest(UPDATE_QUESTIONNAIRE_START, updateQuestionnaire);
    yield takeLatest(UPLOAD_FILE_QUESTIONNAIRE_START, uploadFileQuestionnaire);
    yield takeLatest(DELETE_FILE_QUESTIONNAIRE_START, deleteFileQuestionnaire);
    yield takeLatest(SEND_QUESTIONNAIRE_START, sendQuestionnaire);
    yield takeLatest(REJECT_QUESTIONNAIRE_START, rejectQuestionnaire);
    yield takeLatest(APPROVE_QUESTIONNAIRE_START, approveQuestionnaire);
    yield takeLatest(REQUEST_REVIEWING_QUESTIONNAIRES_START, getReviewingQuestionnaires);
    yield takeLatest(SET_QUESTIONNAIRE_TO_DRAFT_START, setQuestionnaireToDraft);
  }
}

// function getRadioId(choices) {
//   for (let i = 0; i < choices.length; i += 1) {
//     if (choices[i].value !== null) {
//       return choices.find(choice => choice.value !== null).value;
//     }
//     return null;
//   }
//   return null;
// }
